import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import "../../styles/service-charge.scss";
import { useInFlight } from "../hooks/useInFlight";
import { usePendingDelay } from "../hooks/usePendingDelay";
import { PendingContent } from "../shared/PendingDots";

interface ServiceChargeSelectorProps {
  currentPercentage: number;
  currentAmount: number;
  onPercentageChange: (percentage: number) => Promise<boolean>;
  disabled?: boolean;
  canChange?: boolean;
  currency: string;
  countryCode: string;
}

const PERCENTAGE_OPTIONS = [0, 10, 12.5, 15, 17.5, 20];

const ServiceChargeSelector: React.FC<ServiceChargeSelectorProps> = ({
  currentPercentage,
  currentAmount,
  onPercentageChange,
  disabled = false,
  canChange = true,
  currency,
  countryCode,
}) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { isPending, run } = useInFlight();
  const showDots = usePendingDelay(isPending);

  return (
    <div className="service-charge-selector gcw:w-full">
      <div className="gcw:flex gcw:justify-between gcw:items-center gcw:gap-2 gcw:mb-2">
        <div className="gcw:flex gcw:items-center gcw:gap-2">
          <span className="gcw:text-order-details">
            {t("discretionary_service_charge", "Discretionary service charge")}
            {` (${currentPercentage}%)`}
          </span>
          {canChange && !disabled && (
            <button
              type="button"
              className="gcw:inline-flex gcw:items-center gcw:text-primary gcw:font-medium gcw:underline gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
              onClick={() => setShowOptions(!showOptions)}
              aria-expanded={showOptions}
              aria-label={t("change_service_charge", "Change service charge")}
              disabled={isPending}
              aria-busy={isPending || undefined}
            >
              <PendingContent showDots={showDots}>{t("change", "Change")}</PendingContent>
            </button>
          )}
        </div>
        <span className="gcw:text-order-details gcw:font-bold gcw:align-right">
          {formatCurrency(currentAmount, currency, countryCode)}
        </span>
      </div>

      {showOptions && canChange && !disabled && (
        <fieldset
          className="service-charge-options gcw:mt-3 gcw:mb-bs-3"
          aria-label={t("service_charge_options", "Service charge percentage options")}
        >
          <div className="gcw:flex gcw:w-full">
            {PERCENTAGE_OPTIONS.map((percentage) => {
              const isActive = currentPercentage === percentage;
              return (
                <button
                  key={percentage}
                  type="button"
                  onClick={() => {
                    setShowOptions(false);
                    void run(async () => {
                      const errorMessage = t(
                        "service_charge_update_failed",
                        "Sorry, we couldn't update the service charge. Please try again.",
                      );
                      setSaveError(null);
                      try {
                        const saved = await onPercentageChange(percentage);
                        if (!saved) {
                          setSaveError(errorMessage);
                        }
                      } catch {
                        setSaveError(errorMessage);
                      }
                    });
                  }}
                  className={`gcw:flex-1 gcw:py-1.5 gcw:px-2 gcw:text-xs gcw:transition-all gcw:first:rounded-l gcw:last:rounded-r ${
                    isActive
                      ? "gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:hover:bg-primary-hover"
                      : "gcw:border gcw:border-gray-300 gcw:text-gray-600"
                  }`}
                  aria-pressed={isActive}
                  aria-label={percentage === 0 ? t("none", "None") : `${percentage}%`}
                >
                  {percentage === 0 ? t("none", "None") : `${percentage}%`}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
      {saveError && (
        <div
          className="gcw:rounded-md gcw:border gcw:border-error gcw:bg-error/10 gcw:text-error
            widget-error-panel gcw:p-2 gcw:text-sm gcw:mt-2"
          role="alert"
        >
          {saveError}
        </div>
      )}
    </div>
  );
};

export default ServiceChargeSelector;
